import { storableError } from '../../util/errors';
import {
  getConversation,
  sendMessage,
  readMessage,
  initiateConversation,
  uploadAttachment,
  getTransactionConversation,
} from '../../util/api';
import { deletedListing, deletedUser, denormalisedResponseEntities } from '../../util/data';
import { toBase64 } from '../../util/toBase64';
import { currentUserSelector, fetchCurrentUserNotifications } from '../../ducks/user.duck';
import {
  addMarketplaceEntities,
  getListingsById,
  getUsersById,
} from '../../ducks/marketplaceData.duck';
import { fetchConversationsRelations } from '../ConversationsPage/ConversationsPage.duck';

// ================ Action types ================ //

export const SET_INITIAL_VALUES = 'app/ConversationPage/SET_INITIAL_VALUES';

export const FETCH_CONVERSATION_REQUEST = 'app/ConversationPage/FETCH_CONVERSATION_REQUEST';
export const FETCH_CONVERSATION_SUCCESS = 'app/ConversationPage/FETCH_CONVERSATION_SUCCESS';
export const FETCH_CONVERSATION_ERROR = 'app/ConversationPage/FETCH_CONVERSATION_ERROR';

export const SEND_MESSAGE_REQUEST = 'app/ConversationPage/SEND_MESSAGE_REQUEST';
export const SEND_MESSAGE_SUCCESS = 'app/ConversationPage/SEND_MESSAGE_SUCCESS';
export const SEND_MESSAGE_ERROR = 'app/ConversationPage/SEND_MESSAGE_ERROR';

// ================ Reducer ================ //

const initialState = {
  fetchInProgress: false,
  fetchConversationError: null,
  pagination: null,
  conversation: undefined,
  messages: [],
  totalCount: 0,
  sendMessageInProgress: false,
  sendMessageError: null,
};

export default function conversationPageReducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case SET_INITIAL_VALUES:
      return {
        ...initialState,
        ...payload,
      };

    case FETCH_CONVERSATION_REQUEST:
      return {
        ...state,
        fetchInProgress: true,
        fetchConversationError: null,
      };
    case FETCH_CONVERSATION_SUCCESS: {
      return {
        ...state,
        fetchInProgress: false,
        conversation: payload,
      };
    }
    case FETCH_CONVERSATION_ERROR:
      console.error(payload); // eslint-disable-line
      return {
        ...state,
        fetchInProgress: false,
        fetchConversationError: payload,
      };

    case SEND_MESSAGE_REQUEST:
      return {
        ...state,
        sendMessageInProgress: true,
        sendMessageError: null,
        initialMessageFailedToTransaction: null,
      };
    case SEND_MESSAGE_SUCCESS:
      return {
        ...state,
        sendMessageInProgress: false,
        conversation: {
          ...state.conversation,
          messages: [...state.conversation.messages, payload],
        },
      };
    case SEND_MESSAGE_ERROR:
      return {
        ...state,
        sendMessageInProgress: false,
        sendMessageError: payload,
      };

    default:
      return state;
  }
}

// ================ Action creators ================ //
export const setInitialValues = () => ({
  type: SET_INITIAL_VALUES,
  payload: initialState,
});

const fetchConversationRequest = () => ({
  type: FETCH_CONVERSATION_REQUEST,
});

const fetchConversationSuccess = response => ({
  type: FETCH_CONVERSATION_SUCCESS,
  payload: response,
});

const fetchConversationError = e => ({
  type: FETCH_CONVERSATION_ERROR,
  error: true,
  payload: e,
});

const sendMessageRequest = () => ({
  type: SEND_MESSAGE_REQUEST,
});

const sendMessageSuccess = message => ({
  type: SEND_MESSAGE_SUCCESS,
  payload: message,
});

const sendMessageError = e => ({
  type: SEND_MESSAGE_ERROR,
  error: true,
  payload: e,
});

// ================ Selectors ================ //

export const conversationWithRelationsSelector = state => {
  const { conversation } = state.ConversationPage;

  if (!conversation) {
    return undefined;
  }

  const currentUser = currentUserSelector(state);
  const { productId, userId, recipientId, messages } = conversation;

  const isProvider = currentUser.id.uuid === recipientId;
  const otherUserId = isProvider ? userId : recipientId;

  const [listing] = getListingsById(state, [{ uuid: productId }]);
  const [otherUser] = getUsersById(state, [{ uuid: otherUserId }]);

  return {
    ...conversation,
    messages: messages.map(message => ({
      ...message,
      sender: currentUser.id.uuid === message.userId ? currentUser : otherUser,
      type: 'message',
      attributes: {
        content: message.text,
        createdAt: message.sentAt,
      },
    })),
    otherUser,
    listing,
  };
};

// ================ Thunks ================ //
export const readConversationMessage = messageId => async dispatch => {
  await readMessage(messageId);
  dispatch(fetchCurrentUserNotifications());
};

export const sendConversationMessage = (
  conversationId,
  conversationUserId,
  message,
  listingId,
  attachments
) => async (dispatch, getState, sdk) => {
  dispatch(sendMessageRequest());

  try {
    const body = {
      text: message || '',
      conversationId,
    };

    if (!conversationId) {
      const conversation = await initiateConversation({
        productId: listingId,
        userId: conversationUserId,
      });
      body.conversationId = conversation.id;
      dispatch(fetchConversationSuccess(conversation));
    }

    const sentMessage = {
      ...(await sendMessage(body)),
      attachments: [],
    };

    for (const attachment of attachments) {
      try {
        const imageData = await toBase64(attachment);
        const attachmentResponse = await uploadAttachment({
          messageId: sentMessage.id,
          imageData,
        });
        sentMessage.attachments.push(attachmentResponse);
      } catch {}
    }

    dispatch(sendMessageSuccess(sentMessage));

    return sentMessage;
  } catch (e) {
    console.error(e); // eslint-disable
    dispatch(sendMessageError(storableError(e)));
  }
};

export const fetchConversationByTransactionId = id => async (dispatch, getState, sdk) => {
  dispatch(fetchConversationRequest());

  try {
    const conversation = await getTransactionConversation(id);

    dispatch(fetchConversationSuccess(conversation));
  } catch (e) {
    if (e.status === 404) {
      dispatch(fetchConversationSuccess(null));
    } else {
      dispatch(fetchConversationError(storableError(e)));
    }
  }
};

const fetchConversation = id => async (dispatch, getState, sdk) => {
  dispatch(fetchConversationRequest());

  try {
    const conversation = await getConversation(id);

    await dispatch(
      fetchConversationsRelations(
        [conversation],
        {
          include: ['images', 'author', 'author.profileImage', 'currentStock'],
          'fields.image': ['variants.listing-card', 'variants.listing-card-2x'],
          'imageVariant.listing-card': 'w:400;h:533;fit:crop',
          'imageVariant.listing-card-2x': 'w:800;h:1067;fit:crop',
        },
        {
          include: ['profileImage'],
          'fields.image': ['variants.square-small3x', 'variants.square-small2x'],
        }
      )
    );

    dispatch(fetchConversationSuccess(conversation));
  } catch (e) {
    console.error(e);
    dispatch(fetchConversationError(storableError(e)));
  }
};

export const loadData = (params, search, config) => async (dispatch, getState, sdk) => {
  dispatch(fetchConversation(params.id));
};
